import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import AutomationConditionsTable, {
	AutomationConditionsConfig,
	convertAutomationConditionValue
} from 'pages/Automation/AutomationConditionsTable';

import { AUTOCOMPLETE, ENUMS, ORDER_AUTOMATION } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

/**
 *
 * @author @omerrkosar
 */

const defaultColSpan = {
	md: 6
};

const OrderOtomationForm = NiceModal.create(({ id, modalName }) => {
	const [isMinimizing, setIsMinimizing] = useState(false);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					ruleName: yup.string().nullable().required(),
					status: yup.string().nullable().required(),
					priorityOrder: yup.number().nullable(),
					conditions: yup.array().of(
						yup.object().shape({
							condition: yup.string().nullable().required(),
							operator: yup
								.string()
								.nullable()
								.when('condition', ([condition], schema) => {
									const item = AutomationConditionsConfig.find((item) => item.name === condition);
									return item?.type ? schema.required() : schema;
								}),
							value: yup.mixed().nullable().required()
						})
					),
					locationFilter: yup.object().shape({
						locationType: yup.string().nullable(),
						withinCity: yup.boolean().nullable(),
						withinBrand: yup.boolean().nullable(),
						withinServingRadius: yup.boolean().nullable(),
						withinWorkingHours: yup.boolean().nullable(),
						withinStock: yup.boolean().nullable()
					}),
					broadcast: yup.boolean().nullable(),
					broadcastCount: yup.number().nullable().min(1),
					reassign: yup.boolean().nullable(),
					reassignCondition: yup.object().when('reassign', ([reassign], schema) =>
						reassign
							? schema.shape({
									reassignConditionType: yup.string().nullable().required(),
									waitingTime: yup.number().nullable().min(1)
								})
							: schema.nullable()
					),
					priorityMethodType: yup.string().nullable(),
					specificLocation: yup.object().when('priorityMethodType', ([priorityMethodType], schema) =>
						priorityMethodType === 'specificLocation'
							? schema.shape({
									warehouseId: yup
										.number()
										.nullable()
										.when('branchId', ([branchId], schema) =>
											branchId ? schema : schema.required()
										),
									branchId: yup.number().nullable(),
									locationType: yup.string().nullable()
								})
							: schema.nullable()
					)
				})
				.required(),

		[]
	);

	const { showError, showSuccess } = useAlert();
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();

	const { closeModal } = useModalRouter();
	const dispatch = useDispatch();

	const fillForm = async () => {
		const defaultValues = {
			ruleName: null,
			status: null,
			priorityOrder: null,
			conditions: [{ condition: null, operator: null, value: null }],
			locationFilter: {
				locationType: 'allLocations',
				withinCity: false,
				withinBrand: false,
				withinServingRadius: false,
				withinWorkingHours: false,
				withinStock: false
			},
			broadcast: false,
			broadcastCount: 1,
			reassign: false,
			reassignCondition: { reassignConditionType: null, waitingTime: 1 },
			priorityMethodType: 'closestLocation',
			specificLocation: { warehouseId: null, branchId: null, locationType: null }
		};
		if (!id) {
			return defaultValues;
		}

		try {
			const response = await axiosPrivate.post(ORDER_AUTOMATION.GET_ORDER_AUTOMATION, { id });
			const { data } = response;

			const { orderConditionList } = data;

			return {
				...defaultValues,
				...data,
				conditions:
					orderConditionList?.map((item) => {
						const parameter = AutomationConditionsConfig.find(
							(condition) => condition.name === item.orderConditionType
						);

						return {
							condition: item.orderConditionType ?? null,
							operator: item.operator ?? null,
							value: convertAutomationConditionValue(item.value, parameter) ?? null
						};
					}) ?? null
			};
		} catch (error) {
			AppLogger.error(error);
			showError({});
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		setValue,
		watch,
		clearErrors,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting && !isMinimizing);

	const onSubmit = (data) => {
		axiosPrivate
			.post(id ? ORDER_AUTOMATION.UPDATE_ORDER_AUTOMATION : ORDER_AUTOMATION.ADD_ORDER_AUTOMATION, {
				...(id && { id }),
				...data,
				orderConditionList: data.conditions?.map((item) => ({
					orderConditionType: item.condition,
					operator: item.operator === 'equalCustom' ? 'equal' : item.operator,
					value: Array.isArray(item.value)
						? item.value.map((value) => `${value.toString()}`).toString()
						: item.value.toString()
				}))
			})
			.then((response) => {
				const { status } = response;
				if (status === 200) {
					showSuccess();
					dispatch(updateDynamicListData(DYNAMICLISTS.ORDER_AUTOMATION));
					closeModal();
				} else AppLogger.error(response);
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});
	};

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const whNameAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);

	return (
		<Modal
			fullwidth
			maxWidth="large"
			name={modalName}
			className="modal-orderOtomationRule"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={id ? 'editOrderOtomation' : 'addOrderOtomation'}
					tooltip={id ? watch('ruleName') : ''}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">{id ? 'editOrderOtomation' : 'addOrderOtomation'}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="ruleName" isLoading={isLoading} required>
						<StringInput name={'ruleName'} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="status" isLoading={isLoading} required>
						<EnumSelect
							valueRemovable={false}
							placeholder="selectOne"
							searchable={false}
							name="status"
							enumName={ENUMS.FEASIBILITY_STATUS}
							value={watch('status')}
							setValue={(value) => {
								setValue('status', value ?? null, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
					<Alert content="orderOtomation.orderDetails" className="m-3 mt-5" showIcon />
					<FormItem label="feasibility.conditions">
						<AutomationConditionsTable
							isLoading={isLoading}
							conditionsEnumName={ENUMS.ORDER_OTOMATION_CONDITIONS}
							deletableLastRow
						/>
					</FormItem>
					<Alert content="orderOtomation.priority" className="m-3 mt-5" showIcon />
					<FormItem label="priorityMethod" colSpan={defaultColSpan}>
						<EnumSelect
							searchable={false}
							valueRemovable={false}
							required
							placeholder="selectOne"
							name="priorityMethodType"
							enumName={ENUMS.ORDER_OTOMATION_PRIORITY_METHODS}
							value={watch(`priorityMethodType`)}
							setValue={(value) => {
								setValue(`priorityMethodType`, value ?? 'closestLocation', {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
						/>
					</FormItem>
					{watch('priorityMethodType') === 'specificLocation' && (
						<>
							<FormItem label="selectWarehouse" colSpan={defaultColSpan}>
								<AutoComplete
									autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
									value={watch('specificLocation.warehouseId')}
									columns={whNameAutocompleteColumns}
									selectionLabelAccessor={['Name']}
									name="specificLocation.warehouseId"
									setValue={(value) => {
										setValue('specificLocation.warehouseId', value?.ID ?? null, {
											shouldValidate: true,
											shouldDirty: true
										});
										if (value?.ID) {
											clearErrors('specificLocation.branchId');
											setValue('specificLocation.branchId', null, {
												shouldValidate: true,
												shouldDirty: true
											});
											setValue('specificLocation.locationType', 'onlyWarehouses', {
												shouldValidate: true,
												shouldDirty: true
											});
										}
									}}
								/>
							</FormItem>
							<FormItem label="selectBranch" colSpan={defaultColSpan}>
								<AutoComplete
									autoCompleteName={AUTOCOMPLETE.BRANCHES}
									value={watch('specificLocation.branchId')}
									columns={whNameAutocompleteColumns}
									selectionLabelAccessor={['Name']}
									name="specificLocation.branchId"
									setValue={(value) => {
										setValue('specificLocation.branchId', value?.ID ?? null, {
											shouldValidate: true,
											shouldDirty: true
										});
										if (value?.ID) {
											clearErrors('specificLocation.warehouseId');
											setValue('specificLocation.warehouseId', null, {
												shouldValidate: true,
												shouldDirty: true
											});
											setValue('specificLocation.locationType', 'onlyBranches', {
												shouldValidate: true,
												shouldDirty: true
											});
										}
									}}
								/>
							</FormItem>
						</>
					)}

					<Alert content="orderOtomation.locationDetails" className="m-3 mt-5" showIcon />
					<FormItem label="locationType" isLoading={isLoading}>
						<EnumSelect
							valueRemovable={false}
							searchable={false}
							placeholder="selectOne"
							name="locationFilter.locationType"
							enumName={ENUMS.ORDER_OTOMATION_LOCATION_TYPES}
							value={watch('locationFilter.locationType')}
							setValue={(value) => {
								setValue('locationFilter.locationType', value ?? 'allLocations', {
									shouldValidate: true,
									shouldDirty: true
								});
								if (value !== 'onlyBranches') {
									setValue('broadcast', false, {
										shouldValidate: true,
										shouldDirty: true
									});

									setValue('reassignCondition.reassignConditionType', null, {
										shouldValidate: true,
										shouldDirty: true
									});
								}
							}}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="withinCity" isLoading={isLoading}>
						<SwitchInput className="mt-3" name="locationFilter.withinCity" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="withinBrand" isLoading={isLoading}>
						<SwitchInput className="mt-3" name="locationFilter.withinBrand" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="withinServingRadius" isLoading={isLoading}>
						<SwitchInput className="mt-3" name="locationFilter.withinServingRadius" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="withinWorkingHours" isLoading={isLoading}>
						<SwitchInput className="mt-3" name="locationFilter.withinWorkingHours" />
					</FormItem>
					<FormItem label="withinStock" isLoading={isLoading} colSpan={defaultColSpan}>
						<SwitchInput className="mt-3" name="locationFilter.withinStock" />
					</FormItem>

					<Alert content="orderOtomation.otherDetails" className="m-3 mt-5" showIcon />

					<FormItem colSpan={defaultColSpan} label="broadcastOrder" isLoading={isLoading}>
						<SwitchInput
							className="mt-3"
							name="broadcast"
							disabled={watch('locationFilter.locationType') !== 'onlyBranches'}
						/>
					</FormItem>
					{watch('broadcast') && (
						<FormItem colSpan={defaultColSpan} label="locationCount" isLoading={isLoading}>
							<IntegerInput name="broadcastCount" min={1} />
						</FormItem>
					)}
					<FormItem label="reassignOrder" isLoading={isLoading}>
						<SwitchInput
							className="mt-3"
							name="reassign"
							disabled={watch('priorityMethodType') === 'specificLocation'}
						/>
					</FormItem>

					{watch('reassign') && (
						<>
							<FormItem
								label="reassignCondition"
								colSpan={defaultColSpan}
								required={watch('reassign') === true}>
								<EnumSelect
									name="reassignCondition.reassignConditionType"
									searchable={false}
									valueRemovable={false}
									data={
										watch('broadcast')
											? ['orderIsNotAccepted', 'shipmentIsNotCreated']
											: ['shipmentIsNotCreated']
									}
									value={watch(`reassignCondition.reassignConditionType`) ?? null}
									setValue={(value) => {
										setValue(`reassignCondition.reassignConditionType`, value ?? null, {
											shouldValidate: true,
											shouldDirty: true
										});
									}}
								/>
							</FormItem>
							<FormItem colSpan={defaultColSpan} label="waitTime" isLoading={isLoading}>
								<IntegerInput name="reassignCondition.waitingTime" unit="minutes" min={1} />
							</FormItem>
						</>
					)}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={handleCloseModal} />
				<Button
					label="save"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					disabled={!isDirty}
					loading={isLoading}
					disabledTooltip={isDirty && 'formInfo.noChanges'}
					iconPos="right"
				/>
			</ModalActions>
		</Modal>
	);
});

export default OrderOtomationForm;
